import { useIteroAssetDownload } from '../../../../../hooks/useIteroBrowse';
import { useCachedScanArchive } from '../../../../../hooks/useIteroScanAssets';
import type { IteroAssetType } from '../../../../../services/iteroBrowse';
import { downloadBlob } from '../../../../../utils/downloadBlob';
import {
    ASSET_OPTIONS,
    assetFilename,
    downloadState,
    type AssetDownloadState,
    type AssetOption
} from '../assetDownload';
import { browseErrorText } from '../browseErrorText';
import { useIteroBrowserScope } from '../iteroBrowserScope';

export interface AssetDownload {
    option: AssetOption;
    state: AssetDownloadState;
    start: () => void;
}

// One mutation per asset type, called explicitly (not in a loop) so each row keeps its own state.
export function useOrderAssetDownloads(rxId: string, orderCode: string): readonly AssetDownload[] {
    const scope = useIteroBrowserScope();
    const reuse = useCachedScanArchive(scope, rxId);
    const ide = useIteroAssetDownload(scope, rxId, reuse);
    const gallery = useIteroAssetDownload(scope, rxId, reuse);
    const captures = useIteroAssetDownload(scope, rxId, reuse);
    const mutations: Record<IteroAssetType, typeof ide> = {
        IDE: ide,
        IMAGE_GALLERY: gallery,
        SCREEN_CAPTURE: captures
    };

    return ASSET_OPTIONS.map((option) => {
        const download = mutations[option.type];
        const start = () =>
            download.mutate(option.type, {
                onSuccess: (result) => {
                    if (result.kind === 'ready') {
                        downloadBlob(result.blob, assetFilename(result, orderCode, option.type));
                    }
                }
            });
        const state = downloadState({
            isPending: download.isPending,
            errorMessage: download.isError
                ? browseErrorText(download.error, option.errorText)
                : null,
            result: download.data
        });
        return { option, state, start };
    });
}
